import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '@/components/common';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import toast from 'react-hot-toast';

interface RowError {
  row: number;
  errors: Record<string, unknown>;
}

interface ImportResult {
  imported: number;
  failed: number;
  total: number;
  errors: RowError[];
}

interface Props {
  /** Master-data list endpoint, e.g. "/products/types/". Trailing slash required. */
  apiEndpoint: string;
  /** Query keys to invalidate after a successful import. */
  invalidateKeys?: string[];
  /** Extra query params to apply to the export (e.g. active filters). */
  exportParams?: Record<string, string>;
}

async function downloadFile(url: string, fallbackName: string) {
  const res = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([res.data]);
  const disposition = res.headers?.['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename="?([^"]+)"?/);
  const name = match?.[1] || fallbackName;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.click();
  URL.revokeObjectURL(href);
}

export function ImportExportControls({ apiEndpoint, invalidateKeys = [], exportParams }: Props) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const base = apiEndpoint.endsWith('/') ? apiEndpoint : `${apiEndpoint}/`;

  const exportData = async (fileFormat: 'csv' | 'xlsx') => {
    setMenuOpen(false);
    setBusy(true);
    try {
      const params = new URLSearchParams({ file_format: fileFormat, ...(exportParams || {}) });
      await downloadFile(`${base}export/?${params.toString()}`, `export.${fileFormat}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async (fileFormat: 'csv' | 'xlsx') => {
    setMenuOpen(false);
    setBusy(true);
    try {
      await downloadFile(`${base}import-template/?file_format=${fileFormat}`, `template.${fileFormat}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${base}import/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data as ImportResult;
      setResult(data);
      if (data.imported > 0) {
        invalidateKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
        toast.success(`Imported ${data.imported} record(s)`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} row(s) failed validation`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-flex items-center gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={handleFile}
      />
      <Button
        variant="outline"
        size="sm"
        leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
        onClick={() => fileInputRef.current?.click()}
        isLoading={busy}
      >
        Import
      </Button>
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
          onClick={() => setMenuOpen((o) => !o)}
          disabled={busy}
        >
          Export
        </Button>
        {menuOpen && (
          <div
            className="absolute right-0 z-20 mt-1 w-52 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
            onMouseLeave={() => setMenuOpen(false)}
          >
            <button className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => exportData('csv')}>
              <ArrowDownTrayIcon className="w-4 h-4" /> Export CSV
            </button>
            <button className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => exportData('xlsx')}>
              <ArrowDownTrayIcon className="w-4 h-4" /> Export Excel
            </button>
            <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
            <button className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => downloadTemplate('csv')}>
              <DocumentTextIcon className="w-4 h-4" /> CSV template
            </button>
            <button className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => downloadTemplate('xlsx')}>
              <DocumentTextIcon className="w-4 h-4" /> Excel template
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={!!result} onClose={() => setResult(null)} title="Import Results">
        {result && (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <span className="text-green-700 dark:text-green-400">Imported: {result.imported}</span>
              <span className="text-red-600 dark:text-red-400">Failed: {result.failed}</span>
              <span className="text-gray-500">Total rows: {result.total}</span>
            </div>
            {result.errors.length > 0 && (
              <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Row</th>
                      <th className="px-3 py-2 text-left font-semibold">Errors</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {result.errors.map((err) => (
                      <tr key={err.row}>
                        <td className="px-3 py-2 align-top font-medium">{err.row}</td>
                        <td className="px-3 py-2 text-red-600 dark:text-red-400">
                          {Object.entries(err.errors).map(([field, msgs]) => (
                            <div key={field}>
                              <span className="font-medium">{field}:</span>{' '}
                              {Array.isArray(msgs) ? msgs.join(', ') : String(msgs)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setResult(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ImportExportControls;
